/**
 * Modern Landing page for Bolão 2026
 */

import { Link, useNavigate } from 'react-router-dom';
import { Trophy, Users, Target, Calendar, ArrowRight, Sparkles } from 'lucide-react';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ui/ThemeToggle';
import { useAppStore } from '../stores/appStore';
import { cn } from '../lib/utils';
import { useSEO, SEO_CONFIGS } from '../hooks/useSEO';

export default function LandingPage() {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const navigate = useNavigate();

  // SEO meta tags for landing page
  useSEO(SEO_CONFIGS.landing);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 transition-colors">
      {/* Hero Section */}
      <header className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-accent-700 dark:from-primary-700 dark:via-primary-800 dark:to-accent-800 text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary-400/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-300/10 rounded-full blur-3xl" />
        </div>

        <div className="relative container-wide py-6">
          <nav className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-lg">
                <Trophy className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl">Bolão 2026</span>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle className="text-white/70 hover:text-white hover:bg-white/10" />
              {isAuthenticated ? (
                <Link to="/inicio">
                  <Button variant="secondary" size="sm">
                    Acessar
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/entrar" className="text-white/80 hover:text-white transition-colors hidden sm:block">
                    Entrar
                  </Link>
                  <Link to="/cadastro">
                    <Button variant="secondary" size="sm">Criar Conta</Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>

        <div className="relative container-wide py-20 sm:py-28 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white/90 px-4 py-2 rounded-full text-sm font-medium mb-8 border border-white/20">
              <Sparkles className="w-4 h-4 text-secondary-400" />
              Copa do Mundo 2026 - EUA, México e Canadá
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              O Maior Bolão da
              <span className="text-secondary-400 block sm:inline"> Copa do Mundo</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              Faça seus palpites, desafie seus amigos e concorra a prêmios!
              Ranking em tempo real, palpites especiais e muito mais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                className="w-full sm:w-auto shadow-xl shadow-secondary-500/30"
                onClick={() => navigate(isAuthenticated ? '/inicio' : '/cadastro')}
              >
                {isAuthenticated ? 'Acessar Bolão' : 'Começar Agora'}
                <ArrowRight className="w-5 h-5" />
              </Button>
              {!isAuthenticated && (
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 hover:border-white/50"
                  onClick={() => navigate('/entrar')}
                >
                  Ja tenho conta
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              className="fill-neutral-50 dark:fill-neutral-950"
            />
          </svg>
        </div>
      </header>

      {/* Features Section */}
      <section className="py-20 sm:py-28 bg-neutral-50 dark:bg-neutral-950 transition-colors">
        <div className="container-wide">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Por que participar do Bolão 2026?
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              A plataforma mais completa para voce acompanhar a Copa do Mundo
              e competir com seus amigos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={Target}
              title="Palpites Simples"
              description="Faça seus palpites em segundos. Interface intuitiva e rápida."
              delay={0}
            />
            <FeatureCard
              icon={Trophy}
              title="Ranking em Tempo Real"
              description="Acompanhe sua posição no ranking atualizado a cada jogo."
              delay={1}
            />
            <FeatureCard
              icon={Users}
              title="Bolões privados"
              description="Crie bolões privados e convide amigos por link ou código."
              delay={2}
            />
            <FeatureCard
              icon={Calendar}
              title="48 Seleções"
              description="Novo formato com 48 seleções e 104 jogos para palpitar."
              delay={3}
            />
          </div>
        </div>
      </section>

      {/* Scoring Section */}
      <section className="py-20 sm:py-28 bg-white dark:bg-neutral-900 transition-colors">
        <div className="container-wide">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Sistema de Pontuação
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              Quanto mais preciso seu palpite, mais pontos você ganha!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <ScoreCard
              points={10}
              title="Placar Exato"
              description="Acertou o placar completo"
              example="Palpite: 2x1 | Resultado: 2x1"
              color="green"
            />
            <ScoreCard
              points={7}
              title="Saldo + Vencedor"
              description="Acertou diferença de gols e vencedor"
              example="Palpite: 3x1 | Resultado: 2x0"
              color="blue"
            />
            <ScoreCard
              points={3}
              title="Apenas Vencedor"
              description="Acertou apenas quem venceu"
              example="Palpite: 2x0 | Resultado: 3x1"
              color="amber"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-secondary-500/10 rounded-full blur-3xl" />
        </div>
        <div className="container-wide text-center relative">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Pronto para começar?
          </h2>
          <p className="text-lg sm:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Junte-se a milhares de participantes e mostre que você entende de futebol!
          </p>
          <Button
            variant="secondary"
            size="lg"
            className="shadow-xl shadow-secondary-500/30"
            onClick={() => navigate(isAuthenticated ? '/inicio' : '/cadastro')}
          >
            {isAuthenticated ? 'Acessar Meu Bolão' : 'Criar Minha Conta Grátis'}
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 dark:bg-neutral-950 text-neutral-400 py-12 transition-colors">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg shadow-primary-500/20">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-white">Bolão 2026</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span>Feito com paixão pelo futebol</span>
              <span className="text-neutral-500">v2.3.3</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
  delay,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  delay: number;
}) {
  return (
    <div
      className={cn(
        'p-6 rounded-2xl transition-all duration-300',
        'bg-white dark:bg-neutral-900',
        'border border-neutral-200 dark:border-neutral-800',
        'hover:shadow-lg dark:hover:shadow-dark-lg',
        'hover:border-neutral-300 dark:hover:border-neutral-700',
        'hover:-translate-y-1',
        'group'
      )}
      style={{ animationDelay: `${delay * 100}ms` }}
    >
      <div className={cn(
        'w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300',
        'bg-primary-100 dark:bg-primary-900/30',
        'text-primary-600 dark:text-primary-400',
        'group-hover:bg-primary-500 group-hover:text-white',
        'group-hover:shadow-lg group-hover:shadow-primary-500/30'
      )}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">{title}</h3>
      <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">{description}</p>
    </div>
  );
}

function ScoreCard({
  points,
  title,
  description,
  example,
  color,
}: {
  points: number;
  title: string;
  description: string;
  example: string;
  color: 'green' | 'blue' | 'amber';
}) {
  const colors = {
    green: cn(
      'bg-green-50 dark:bg-green-900/20',
      'border-green-200 dark:border-green-800',
      'text-green-900 dark:text-green-100'
    ),
    blue: cn(
      'bg-blue-50 dark:bg-blue-900/20',
      'border-blue-200 dark:border-blue-800',
      'text-blue-900 dark:text-blue-100'
    ),
    amber: cn(
      'bg-amber-50 dark:bg-amber-900/20',
      'border-amber-200 dark:border-amber-800',
      'text-amber-900 dark:text-amber-100'
    ),
  };

  const pointColors = {
    green: 'text-green-600 dark:text-green-400',
    blue: 'text-blue-600 dark:text-blue-400',
    amber: 'text-amber-600 dark:text-amber-400',
  };

  return (
    <div className={cn('p-6 rounded-2xl border-2 transition-all duration-300 hover:scale-[1.02]', colors[color])}>
      <div className={cn('text-4xl font-bold mb-2', pointColors[color])}>{points} pts</div>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-sm opacity-80 mb-4 leading-relaxed">{description}</p>
      <code className="text-xs bg-black/10 dark:bg-white/10 px-3 py-1.5 rounded-lg block">
        {example}
      </code>
    </div>
  );
}
